import {
  IContextualMenuStyleProps,
  IContextualMenuStyles,
  IMenuItemStyles
} from '@fluentui/react/lib/ContextualMenu';
import { IContextualMenuItemStyleProps } from '@fluentui/react/lib/ContextualMenu';
import { IStyle, ITheme } from '@fluentui/react/lib/Styling';
import { ContrastColors } from '../teams-colors';
import { FontSizes } from '../teams-type';

export function getContextualMenuStyles(themeName: string, _theme: ITheme) {
  return (props: IContextualMenuStyleProps): Partial<IContextualMenuStyles> => {
    const { theme } = props;
    const { palette } = theme;
    const CONTEXTUAL_MENU_ITEM_HEIGHT = 32;

    const iconStyles = {
      maxHeight: CONTEXTUAL_MENU_ITEM_HEIGHT,
      fontSize: FontSizes.size14,
      width: FontSizes.size14,
      margin: '0 9px 0 5px',
      color: palette.neutralDark
    };

    const onFocusHoverActiveStyles = {
      color: palette.white,
      backgroundColor: palette.themeSecondary,
      selectors: {
        button: {
          color: palette.white,
          backgroundColor: palette.themeSecondary
        },
        '.ms-ContextualMenu-icon, .ms-ContextualMenu-submenuIcon, .ms-ContextualMenu-secondaryText, .ms-ContextualMenu-checkmarkIcon, .ms-ContextualMenu-checkmarkIcon': {
          color: palette.white,
          backgroundColor: palette.themeSecondary
        },
        'svg, .icons-default-fill': {
          fill: palette.white
        }
      }
    };

    const isFocusVisible = {
      '.ms-Fabric--isFocusVisible &': {
        selectors: {
          '::after': {
            display: 'none'
          } as IStyle,
          ':hover': {
            ...onFocusHoverActiveStyles
          },
          ':focus': {
            ...onFocusHoverActiveStyles
          },
          ':focus:hover': {
            ...onFocusHoverActiveStyles
          },
          ':active': {
            ...onFocusHoverActiveStyles
          }
        }
      }
    };

    const commonStyles = {
      selectors: {
        ':hover': {
          ...onFocusHoverActiveStyles
        },
        ':focus': {
          ...onFocusHoverActiveStyles
        },
        ':active': {
          ...onFocusHoverActiveStyles
        },
        ...isFocusVisible
      }
    };

    const styles = {
      subComponentStyles: {
        callout: {
          root: {
            zIndex: 999,
            selectors: {
              '.ms-ContextualMenu': {
                minWidth: '206px',
                backgroundColor: 'transparent'
              },
              '.ms-ContextualMenu-link, .ms-ContextualMenu-header': {
                padding: '0 11px'
              },
              '.ms-ContextualMenu-header': {
                fontSize: FontSizes.size14
              },
              svg: {
                maxWidth: CONTEXTUAL_MENU_ITEM_HEIGHT,
                maxHeight: CONTEXTUAL_MENU_ITEM_HEIGHT,
                display: 'block',
                marginLeft: '-9px'
              },
              '.ms-ContextualMenu-submenuIcon svg': {
                marginLeft: 0,
                marginRight: '-11px'
              },
              "[data-icon-name='msft-teams']": {
                marginRight: '4px'
              },
              '.icons-msft-teams': {
                height: '24px',
                marginLeft: '-6px',
                marginTop: '-8px'
              }
            }
          },
          calloutMain: {
            paddingTop: '4px',
            paddingBottom: '4px'
          }
        },
        width: {
          minWidth: '208px'
        },

        menuItem: (
          itemStyleProps: IContextualMenuItemStyleProps
        ): Partial<IMenuItemStyles> => {
          const {
            disabled,
            expanded,
            primaryDisabled,
            checked
          } = itemStyleProps;
          let THEME_LIGHT = palette.themeLight;
          let NEUTRAL_TERTIARY = palette.neutralTertiary;
          let NEUTRAL_PRIMARY = palette.neutralPrimary;
          let NEUTRAL_DARK = palette.neutralDark;
          let NEUTRAL_DARK_WHITE = palette.neutralDark;
          let NEUTRAL_QUATERNARY_ALT = palette.neutralQuaternaryAlt;

          let ICON_MSFT_TEAMS_FILL_COLOR = palette.themePrimary;

          // Contrast theme overrides
          if (themeName === 'contrast') {
            THEME_LIGHT = palette.themeSecondary;
            NEUTRAL_TERTIARY = ContrastColors.appGreenContrast;
            NEUTRAL_PRIMARY = palette.black;
            NEUTRAL_DARK = palette.neutralDark;
            NEUTRAL_DARK_WHITE = palette.white;
            NEUTRAL_QUATERNARY_ALT = palette.black;
            ICON_MSFT_TEAMS_FILL_COLOR = palette.black;
          }

          // Dark theme overrides
          if (themeName === 'dark') {
            NEUTRAL_QUATERNARY_ALT = palette.white;
          }

          const menuStyles = {
            divider: {
              margin: '4px 0',
              height: '2px',
              backgroundColor: NEUTRAL_QUATERNARY_ALT
            },
            root: [
              {
                height: CONTEXTUAL_MENU_ITEM_HEIGHT,
                lineHeight: CONTEXTUAL_MENU_ITEM_HEIGHT,
                color: palette.neutralDark,
                selectors: {
                  'svg, .icons-default-fill': {
                    fill: palette.neutralDark
                  },
                  'svg.icons-msft-teams, .icons-msft-teams .icons-default-fill': {
                    fill: ICON_MSFT_TEAMS_FILL_COLOR
                  }
                }
              },
              !disabled && {
                ...commonStyles
              },
              expanded && {
                color: NEUTRAL_DARK_WHITE,
                backgroundColor: THEME_LIGHT
              },
              disabled && {
                color: NEUTRAL_TERTIARY
              }
            ],
            splitPrimary: [
              {
                height: CONTEXTUAL_MENU_ITEM_HEIGHT,
                lineHeight: CONTEXTUAL_MENU_ITEM_HEIGHT,
                padding: '0 11px',
                color: NEUTRAL_DARK,
                selectors: {
                  '&:hover ~ button': {
                    backgroundColor: `${THEME_LIGHT} !important`,
                    color: NEUTRAL_DARK_WHITE,
                    selectors: {
                      '.ms-ContextualMenu-submenuIcon': {
                        color: NEUTRAL_DARK_WHITE
                      }
                    }
                  }
                }
              },
              !(disabled || primaryDisabled) &&
                !checked && {
                  ...commonStyles
                },
              (disabled || primaryDisabled) && {
                color: NEUTRAL_TERTIARY,
                selectors: {
                  '.ms-ContextualMenu-icon': {
                    color: NEUTRAL_TERTIARY
                  }
                }
              },
              checked && {
                selectors: {
                  '.ms-ContextualMenu-checkmarkIcon': {
                    color: NEUTRAL_PRIMARY
                  }
                }
              }
            ],
            splitMenu: [
              {
                height: CONTEXTUAL_MENU_ITEM_HEIGHT,
                lineHeight: CONTEXTUAL_MENU_ITEM_HEIGHT,
                width: CONTEXTUAL_MENU_ITEM_HEIGHT, // to match the numbers from the default styles
                ...commonStyles
              },
              expanded && {
                color: NEUTRAL_DARK,
                backgroundColor: THEME_LIGHT
              }
            ],
            icon: [{ ...iconStyles }, disabled && { color: NEUTRAL_TERTIARY }],
            checkmarkIcon: [{ ...iconStyles }],

            splitContainer: [
              {
                height: CONTEXTUAL_MENU_ITEM_HEIGHT,
                backgroundColor: 'transparent !important'
              },
              !(disabled || primaryDisabled) &&
                !checked && {
                  selectors: { ...isFocusVisible }
                },
              disabled && { color: NEUTRAL_TERTIARY }
            ],
            subMenuIcon: [
              {
                height: CONTEXTUAL_MENU_ITEM_HEIGHT,
                lineHeight: CONTEXTUAL_MENU_ITEM_HEIGHT,
                fontSize: FontSizes.size12,
                color: NEUTRAL_DARK
              },
              expanded && {
                color: NEUTRAL_DARK_WHITE,
                backgroundColor: THEME_LIGHT
              },
              disabled && { color: NEUTRAL_TERTIARY }
            ]
          };
          return menuStyles;
        }
      }
    };
    return styles;
  };
}
